'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { IBranch } from '@/_types';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import {
    DialogContent,
    DialogRoot,
    DialogCloseTrigger,
} from '@/components/ui/dialog';
import { PrevButton, NextButton, usePrevNextButtons } from '@/_components/EmblaCarousel/ArrowButtons';
import { LuX } from 'react-icons/lu';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { getBrandHref } from '@/_lib/brand-href';

const BASE = process.env.BASE_URL ?? '';

function getImageSrc(url: string) {
    return url?.includes(BASE) ? url : BASE + url;
}

export default function Gallery({ data }: { data: IBranch }) {
    const images = data?.images ?? [];
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const t = useTranslations();
    const [emblaRef, emblaApi] = useEmblaCarousel({
        startIndex: selectedIndex,
        loop: true,
        align: 'center',
    });

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = usePrevNextButtons(emblaApi);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect).on('reInit', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    // When opening carousel, scroll to selected index
    useEffect(() => {
        if (carouselOpen && emblaApi) {
            emblaApi.scrollTo(selectedIndex, true);
        }
    }, [carouselOpen, selectedIndex, emblaApi]);

    const openCarousel = (index: number) => {
        setSelectedIndex(index);
        setCarouselOpen(true);
    };

    const closeCarousel = () => setCarouselOpen(false);

    if (!images.length) {
        return (
            <Box py={4}>
                <Text color="gray.500" textAlign="center">
                    {t('no_images_yet')}
                </Text>
                <Box position="fixed" zIndex={40} bottom={0} left={0} right={0} p={4} bg="white" borderTop="1px solid #E0E0E0">
                    <Button w="full" h={'56px'} variant='solid' bg={'primary'} rounded={16} mb={2} asChild>
                        <Link href={getBrandHref(data?.shipper_id, {
                            path: '/reserve',
                            query: { branch_id: data?.slug || data?.id },
                        })}>
                            {t('reserve_table')}
                        </Link>
                    </Button>
                    <Button w="full" h={'56px'} variant='outline' bg={'white'} rounded={16} asChild>
                        <Link href={`https://yandex.com/maps/?ll=${data?.location?.long}%2C${data?.location?.lat}&mode=whatshere&whatshere%5Bpoint%5D=${data?.location?.long}%2C${data?.location?.lat}&z=14`}>
                            {t('build_route')}
                        </Link>
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Flex direction="column" gap={3} pt={4} pb={100} width="100%">
                {images.map((image, index) => (
                    <Box
                        key={`${image}-${index}`}
                        position="relative"
                        width="100%"
                        aspectRatio={16 / 10}
                        borderRadius="xl"
                        overflow="hidden"
                        cursor="pointer"
                        onClick={() => openCarousel(index)}
                        _active={{ opacity: 0.95 }}
                    >
                        <Image
                            src={getImageSrc(image)}
                            alt={data?.name ? `${data.name} — ${index + 1}` : `Gallery ${index + 1}`}
                            fill
                            sizes="100vw"
                            style={{ objectFit: 'cover' }}
                        />
                    </Box>
                ))}
                <Box position="fixed" zIndex={40} bottom={0} left={0} right={0} p={4} bg="white" borderTop="1px solid #E0E0E0">
                    <Button w="full" h={'56px'} variant='solid' bg={'primary'} rounded={16} mb={2} asChild>
                        <Link href={getBrandHref(data?.shipper_id, {
                            path: '/reserve',
                            query: { branch_id: data?.slug || data?.id },
                        })}>
                            {t('reserve_table')}
                        </Link>
                    </Button>
                    <Button w="full" h={'56px'} variant='outline' bg={'white'} rounded={16} asChild>
                        <Link href={`https://yandex.com/maps/?ll=${data?.location?.long}%2C${data?.location?.lat}&mode=whatshere&whatshere%5Bpoint%5D=${data?.location?.long}%2C${data?.location?.lat}&z=14`}>
                            {t('build_route')}
                        </Link>
                    </Button>
                </Box>
            </Flex>

            <DialogRoot
                size="full"
                open={carouselOpen}
                onOpenChange={(e) => {
                    if (!e.open) closeCarousel();
                }}
                lazyMount
            >
                <DialogContent
                    bg="black"
                    rounded="none"
                    maxW="100%"
                    maxH="100%"
                    w="100%"
                    h="100%"
                    p={0}
                    display="flex"
                    flexDirection="column"
                    alignItems="stretch"
                    justifyContent="center"
                >
                    <DialogCloseTrigger
                        position="fixed"
                        top={4}
                        right={4}
                        zIndex={10}
                        color="white"
                        bg="blackAlpha.500"
                        _hover={{ bg: 'blackAlpha.700' }}
                        rounded="full"
                    >
                        <LuX />
                    </DialogCloseTrigger>

                    <Box flex="1" position="relative" display="flex" alignItems="center" w="100%">
                        {!prevBtnDisabled && (
                            //   <Box position="absolute" left={0} top="50%" transform="translateY(-50%)" zIndex={5}>
                            <PrevButton
                                onClick={onPrevButtonClick}
                                disabled={prevBtnDisabled}
                                style={{
                                    background: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    border: 'none',
                                    left: '8px'
                                }}
                            />
                            //   </Box>
                        )}
                        {!nextBtnDisabled && (
                            //   <Box position="absolute" right={0} top="50%" transform="translateY(-50%)" zIndex={5}>
                            <NextButton
                                onClick={onNextButtonClick}
                                disabled={nextBtnDisabled}
                                style={{
                                    background: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    border: 'none',
                                    right: '8px'
                                }}
                            />
                            //   </Box>
                        )}

                        <Box className="embla embla--fullscreen" overflow="hidden" flex="1" ref={emblaRef}>
                            <Flex className="embla__container" h="100%" align="center">
                                {images.map((image, index) => (
                                    <Box
                                        key={`carousel-${image}-${index}`}
                                        className="embla__slide embla__slide--full"
                                        flex="0 0 100%"
                                        minW={0}
                                        position="relative"
                                        h="70vh"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Box position="relative" w="100%" h="100%">
                                            <Image
                                                src={getImageSrc(image)}
                                                alt={data?.name ? `${data.name} — ${index + 1}` : `Gallery ${index + 1}`}
                                                fill
                                                sizes="100vw"
                                                style={{ objectFit: 'contain' }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Flex>
                        </Box>
                    </Box>

                    <Flex
                        justify="center"
                        py={3}
                        color="white"
                        fontSize="sm"
                        bg="blackAlpha.600"
                    >
                        {selectedIndex + 1} / {images.length}
                    </Flex>
                </DialogContent>
            </DialogRoot>

            <style jsx global>{`
        .embla--fullscreen .embla__slide--full {
          padding-left: 0;
        }
      `}</style>
        </>
    );
}
